import React from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from "randmarcomps";
import {
  Layers,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";

type StepDefinition = {
  id: string;
  title: string;
  description: string;
  helper: string;
  icon: LucideIcon;
  comingSoon?: boolean;
};

const steps: StepDefinition[] = [
  {
    id: "define",
    title: "Define the offer",
    description:
      "Name the opportunity, confirm the effective dates, and set clear guardrails.",
    helper:
      "Start with the reseller's business objective and work backward from the margin they need to win.",
    icon: Sparkles,
  },
  {
    id: "resellers",
    title: "Select qualified resellers",
    description:
      "Choose who gets access and save them as your default crew for the next offer.",
    helper:
      "Use profiling tags to double-check their competencies before they see your pricing.",
    icon: Users,
  },
  {
    id: "pricing",
    title: "Share tailored pricing",
    description:
      "Attach products, confirm bid pricing, and preview the notification email.",
    helper:
      "Hint: lead with the differentiator - bundle attach rates go up 18% when you do.",
    icon: Layers,
  },
  {
    id: "insights",
    title: "Automate nudges",
    description:
      "Schedule follow-ups and let Randmar ping resellers while you stay focused.",
    helper:
      "Automations will fan out updates, so you only jump in when engagement dips.",
    icon: ShieldCheck,
    comingSoon: true,
  },
];

const quickFacts = [
  { label: "Avg. launch time", value: "~3 min" },
  { label: "Default reseller slots", value: "Unlimited" },
  { label: "Notifications", value: "In-app + email" },
];

const CreateNewOpportunitiesSteps: React.FC = () => {
  const { applicationId } = useManufacturer();

  return (
    <section
      aria-labelledby="opportunities-empty-title"
      className="space-y-6"
      data-component="opportunities-empty-state"
    >
      <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
        <CardHeader className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            No opportunities yet
          </div>
          <CardTitle
            id="opportunities-empty-title"
            className="text-2xl font-semibold text-slate-900"
          >
            Launch your first opportunity
          </CardTitle>
          <CardDescription className="max-w-2xl text-[15px] leading-relaxed text-slate-600">
            Set dates, pick qualified resellers, and publish pricing in a few quick steps.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 lg:grid-cols-[1.7fr_1fr]">
          <ol className="space-y-4" role="list" aria-label="Opportunity steps">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const stepLabel = `Step ${index + 1}`;

              return (
                <li key={step.id} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex gap-4">
                      <span
                        className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--ops-accent-muted)] text-sm font-semibold text-[var(--ops-accent)]"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="space-y-1">
                        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {stepLabel}
                          <StepIcon className="h-4 w-4 text-[var(--ops-accent)]" aria-hidden="true" />
                        </p>
                        <p className="text-[15px] font-semibold text-slate-900">{step.title}</p>
                        <p className="text-[13px] text-slate-600">{step.description}</p>
                      </div>
                    </div>

                    {step.comingSoon ? (
                      <Badge variant="secondary" size="sm" className="w-fit">
                        Coming soon
                      </Badge>
                    ) : null}
                  </div>

                  <p className="mt-3 rounded-xl bg-gray-50 px-4 py-3 text-[13px] text-slate-600">
                    {step.helper}
                  </p>
                </li>
              );
            })}
          </ol>

          <aside className="flex flex-col gap-5 rounded-2xl border border-gray-100 bg-gray-50 p-6 text-slate-900">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Quick facts</p>
              <p className="text-[15px] text-slate-600">
                Build your offer once, then reuse defaults for faster launches.
              </p>
            </div>

            <Separator />

            <dl className="grid gap-3 text-[15px]">
              {quickFacts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-3 py-2"
                >
                  <dt className="text-slate-500">{fact.label}</dt>
                  <dd className="font-semibold text-slate-900">{fact.value}</dd>
                </div>
              ))}
            </dl>

            <div className="space-y-3 pt-1">
              {applicationId ? (
                <Button asChild size="lg" className="w-full justify-center rounded-xl">
                  <Link to={Path.createOpportunity(applicationId)}>Create opportunity</Link>
                </Button>
              ) : (
                <Button size="lg" className="w-full justify-center rounded-xl" disabled>
                  Create opportunity
                </Button>
              )}

              <Button asChild variant="outline" className="w-full justify-center rounded-xl">
                <a
                  href="https://randmar.io/resources/opportunities"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Learn how Opportunities work (opens in a new tab)"
                >
                  Learn how Opportunities work
                </a>
              </Button>
            </div>
          </aside>
        </CardContent>
      </Card>
    </section>
  );
};

export default CreateNewOpportunitiesSteps;
